import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import { Box, Divider, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";

import { Icon } from "../Icon";
import { IconButton } from "../IconButton/IconButton";
import { useCarousel } from "./useCarousel";

const Root = styled("div")<{ slideSize: string; slideGap: string }>(
  ({ slideSize, slideGap }) => ({
    overflow: "hidden",
    "--slide-spacing": slideGap,
    "--slide-size": slideSize,
  }),
);

const Container = styled("div")({
  display: "flex",
  marginLeft: "calc(var(--slide-spacing) * -1)",
});

const Slide = styled("div")({
  transform: "translate3d(0, 0, 0)",
  flex: "0 0 var(--slide-size)",
  minWidth: 0,
  paddingLeft: "var(--slide-spacing)",
});

interface Slots {
  header?: React.ElementType;
}

export type CarouselProps = React.PropsWithChildren<{
  slideSize?: string;
  slideGap?: string;
  slots?: Slots;
  slotProps?: Partial<ExtractSlotProps<Slots>>;
}>;

export const Carousel = (props: CarouselProps) => {
  const {
    children,
    slots,
    slideSize = "50%",
    slideGap = "10px",
    slotProps,
  } = props;

  const {
    hasPrev,
    hasNext,
    carouselRef,
    scrollPrev,
    scrollNext,
    handleKeyDown,
  } = useCarousel({ loop: true });

  return (
    <Stack gap={2}>
      <Stack
        direction="row"
        gap={2}
        divider={<Divider orientation="vertical" flexItem />}
        sx={{
          alignItems: "center",
        }}
      >
        <Box sx={{ flex: 1 }}>
          {slots?.header && <slots.header {...(slotProps?.header ?? {})} />}
        </Box>
        <Stack direction="row" gap={2}>
          <IconButton
            onClick={scrollPrev}
            disabled={!hasPrev}
            aria-label="Previous"
          >
            <Icon icon={faChevronLeft} />
          </IconButton>

          <IconButton
            onClick={scrollNext}
            disabled={!hasNext}
            aria-label="Next"
          >
            <Icon icon={faChevronRight} />
          </IconButton>
        </Stack>
      </Stack>
      <Root
        ref={carouselRef}
        slideSize={slideSize}
        slideGap={slideGap}
        role="region"
        aria-label="Carousel"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <Container>
          {React.Children.map(children, (child, index) => (
            <Slide
              aria-roledescription="slide"
              aria-label={`Slide ${index + 1}`}
            >
              {child}
            </Slide>
          ))}
        </Container>
      </Root>
    </Stack>
  );
};
